import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";
import {
  faFacebookSquare,
  faLinkedinIn,
} from "@fortawesome/free-brands-svg-icons";
import { Link, useNavigate, useLocation } from "@tanstack/react-router";
import MagneticButton from "../motion/MagneticButton";
import PixelRunner, { type PixelRunnerState } from "../motion/PixelRunner";
import { prefersReducedMotion, scrollToElement } from "../../lib/motion";

interface HeaderProps {
  navbarBackground: boolean;
  scrollToContact: () => void;
}

const Header: React.FC<HeaderProps> = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [runnerState, setRunnerState] = useState<PixelRunnerState>("idle");
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavLinkClick = () => {
    setIsNavOpen(false);
  };

  const scrollToAbout = (e: React.MouseEvent) => {
    e.preventDefault();

    if (location.pathname !== "/") {
      navigate({ to: "/" });
      setTimeout(() => {
        scrollToElement("#about");
      }, 150);
    } else {
      scrollToElement("#about");
    }
    handleNavLinkClick();
  };

  const scrollToProcess = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate({ to: "/" });
      setTimeout(() => scrollToElement("#process"), 150);
    } else {
      scrollToElement("#process");
    }
    handleNavLinkClick();
  };

  const scrollToProjects = (e: React.MouseEvent) => {
    e.preventDefault();

    if (location.pathname !== "/projects") {
      navigate({ to: "/projects" });
      setTimeout(() => {
        scrollToElement("#projects");
      }, 150);
    } else {
      scrollToElement("#projects");
    }
    handleNavLinkClick();
  };

  const navigateToPackages = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate({ to: "/packages" });
    handleNavLinkClick();
  };

  const navigateToArticles = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate({ to: "/articles" });
    handleNavLinkClick();
  };

  const isArticlesActive = location.pathname.startsWith("/articles");

  const bookConsult = (e?: React.MouseEvent) => {
    e?.preventDefault();
    navigate({ to: "/packages", hash: "consult" });
    handleNavLinkClick();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const navbar = document.querySelector(".navbar");
      const navbarToggler = document.querySelector(".navbar-toggler");

      if (
        isNavOpen &&
        navbar &&
        !navbar.contains(event.target as Node) &&
        !navbarToggler?.contains(event.target as Node)
      ) {
        setIsNavOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isNavOpen]);

  return (
    <header>
      <nav
        className="fixed top-0 w-full z-50 transition-all duration-300 backdrop-blur-md py-2"
        aria-label="Main navigation"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <a
              href="/"
              className="text-white text-xl font-bold hover:text-cyan-400 transition-colors duration-300 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                navigate({ to: "/" });
              }}
              aria-label="Don Benary - Home"
            >
              Don Benary
            </a>

          <button
            type="button"
            className="lg:hidden text-white hover:text-cyan-400 transition-colors duration-300 p-2"
            onClick={() => setIsNavOpen(!isNavOpen)}
            aria-label={isNavOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isNavOpen}
            aria-controls="mobile-nav"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>

          <div className="hidden lg:flex flex-1 items-center justify-center">
            <ul className="flex items-center space-x-6 xl:space-x-8">
              <li>
                <a
                  href="#process"
                  onClick={scrollToProcess}
                  className="text-white/90 hover:text-white transition-colors duration-300 font-medium cursor-pointer"
                >
                  Process
                </a>
              </li>
              <li>
                <a
                  href="#about"
                  onClick={scrollToAbout}
                  className="text-white/90 hover:text-white transition-colors duration-300 font-medium cursor-pointer"
                >
                  About
                </a>
              </li>
              <li>
                <a
                  href="#projects"
                  onClick={scrollToProjects}
                  className="text-white/90 hover:text-white transition-colors duration-300 font-medium cursor-pointer"
                >
                  Featured Works
                </a>
              </li>
              <li>
                <a
                  href="/packages"
                  onClick={navigateToPackages}
                  className="text-white/90 hover:text-white transition-colors duration-300 font-medium cursor-pointer"
                >
                  Packages
                </a>
              </li>
              <li>
                <a
                  href="/articles"
                  onClick={navigateToArticles}
                  aria-current={isArticlesActive ? "page" : undefined}
                  className={`transition-colors duration-300 font-medium cursor-pointer ${
                    isArticlesActive
                      ? "text-cyan-400"
                      : "text-white/90 hover:text-white"
                  }`}
                >
                  Articles
                </a>
              </li>
              <li>
                <a
                  href="https://drive.google.com/file/d/1pXw_RS_-uMIqETQ84OGuNk991pyJPBMa/view?usp=sharing"
                  download=""
                  target="_blank"
                  rel="noreferrer"
                  className="text-white/90 hover:text-white transition-colors duration-300 font-medium flex items-center space-x-2"
                >
                  <FontAwesomeIcon icon={faFilePdf} />
                  <span>Resume</span>
                </a>
              </li>
            </ul>
          </div>

          <div className="hidden lg:flex items-center space-x-3 xl:space-x-4 ml-4">
            <Link
              to="/play"
              onMouseEnter={() => setRunnerState("run")}
              onMouseLeave={() => setRunnerState("idle")}
              onFocus={() => setRunnerState("run")}
              onBlur={() => setRunnerState("idle")}
              className="flex shrink-0 items-center gap-2 whitespace-nowrap px-2.5 xl:px-3 py-2 rounded-lg border border-cyan-400/40 text-cyan-200 text-sm font-semibold no-underline hover:text-cyan-100 hover:bg-cyan-400/10 hover:border-cyan-300 transition-colors"
              aria-label="Play the game: my portfolio as a side-scroller"
              title="Play the game"
            >
              <PixelRunner
                state={runnerState}
                reducedMotion={prefersReducedMotion()}
                className="!w-4 !h-5"
              />
              <span className="hidden xl:inline">Play</span>
            </Link>
            <MagneticButton
              className="px-4 py-2 rounded-lg bg-cyan-500 text-slate-950 text-sm font-semibold hover:bg-cyan-400 transition-colors"
              onClick={bookConsult}
            >
              Book a consult
            </MagneticButton>
            <a
              href="https://www.facebook.com/donbenary1"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/80 hover:text-white transition-colors duration-300 text-lg"
              aria-label="Facebook profile (opens in new tab)"
            >
              <FontAwesomeIcon icon={faFacebookSquare} aria-hidden="true" />
            </a>
            <a
              href="https://www.linkedin.com/in/donbenary"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/80 hover:text-white transition-colors duration-300 text-lg"
              aria-label="LinkedIn profile (opens in new tab)"
            >
              <FontAwesomeIcon icon={faLinkedinIn} aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>

      <div
        id="mobile-nav"
        className={`lg:hidden ${isNavOpen ? "block" : "hidden"} mt-4 pb-4`}
        role="region"
        aria-label="Mobile navigation"
      >
        <div className="bg-black/95 rounded-lg border border-white/10 p-4">
          <ul className="space-y-3">
            <li>
              <a
                href="#process"
                onClick={scrollToProcess}
                className="block text-white/90 hover:text-white hover:bg-white/10 transition-all duration-300 font-medium px-3 py-2 rounded cursor-pointer"
              >
                Process
              </a>
            </li>
            <li>
              <a
                href="#about"
                onClick={scrollToAbout}
                className="block text-white/90 hover:text-white hover:bg-white/10 transition-all duration-300 font-medium px-3 py-2 rounded cursor-pointer"
              >
                About
              </a>
            </li>
            <li>
              <a
                href="#projects"
                onClick={scrollToProjects}
                className="block text-white/90 hover:text-white hover:bg-white/10 transition-all duration-300 font-medium px-3 py-2 rounded cursor-pointer"
              >
                Featured Works
              </a>
            </li>
            <li>
              <a
                href="/packages"
                onClick={navigateToPackages}
                className="block text-white/90 hover:text-white hover:bg-white/10 transition-all duration-300 font-medium px-3 py-2 rounded cursor-pointer"
              >
                Packages
              </a>
            </li>
            <li>
              <a
                href="/articles"
                onClick={navigateToArticles}
                aria-current={isArticlesActive ? "page" : undefined}
                className={`block hover:bg-white/10 transition-all duration-300 font-medium px-3 py-2 rounded cursor-pointer ${
                  isArticlesActive
                    ? "text-cyan-400"
                    : "text-white/90 hover:text-white"
                }`}
              >
                Articles
              </a>
            </li>
            <li>
              <Link
                to="/play"
                onClick={handleNavLinkClick}
                className="flex items-center gap-3 px-3 py-2 rounded border border-cyan-400/40 text-cyan-200 font-medium no-underline hover:bg-cyan-400/10 hover:text-cyan-100 transition-all duration-300"
              >
                <PixelRunner
                  state="idle"
                  reducedMotion={prefersReducedMotion()}
                  className="!w-4 !h-5"
                />
                <span>Play the game</span>
                <span className="ml-auto text-xs text-white/50">~3 min</span>
              </Link>
            </li>
            <li>
              <button
                type="button"
                onClick={bookConsult}
                className="w-full text-left px-3 py-2 rounded bg-cyan-500 text-slate-950 font-semibold"
              >
                Book a consult
              </button>
            </li>
            <li>
              <a
                href="https://drive.google.com/file/d/1pXw_RS_-uMIqETQ84OGuNk991pyJPBMa/view?usp=sharing"
                download=""
                target="_blank"
                rel="noreferrer"
                className="block text-white/90 hover:text-white hover:bg-white/10 transition-all duration-300 font-medium px-3 py-2 rounded flex items-center space-x-2"
                onClick={handleNavLinkClick}
              >
                <FontAwesomeIcon icon={faFilePdf} />
                <span>Resume</span>
              </a>
            </li>
          </ul>

          <div className="flex items-center justify-center space-x-6 pt-4 mt-4 border-t border-white/20 md:hidden block">
            <a
              href="https://www.facebook.com/donbenary1"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/80 hover:text-white transition-colors duration-300 text-xl"
              aria-label="Facebook profile (opens in new tab)"
            >
              <FontAwesomeIcon icon={faFacebookSquare} aria-hidden="true" />
            </a>
            <a
              href="https://www.linkedin.com/in/donbenary"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/80 hover:text-white transition-colors duration-300 text-xl"
              aria-label="LinkedIn profile (opens in new tab)"
            >
              <FontAwesomeIcon icon={faLinkedinIn} aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
      </nav>
    </header>
  );
};

export default Header;
